import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Checkbox,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Unarchive as UnarchiveIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { 
  fetchArchivedInvoices,
  unarchiveInvoice,
  setPagination
} from '../../store/slices/invoiceSlice';
import { RootState, AppDispatch } from '../../store/store';
import { 
  selectInvoices, 
  selectInvoicesLoading, 
  selectInvoicesPagination
} from '../../store/selectors/invoiceSelectors';
import { Invoice } from '../../types/invoice';
import czechTranslations from '../../translations/czech';
import useModernAlert from '../../hooks/useModernAlert';
import PDFDownload from '../PDF/PDFDownload';
import { formatDateSafe } from '../../utils/dateUtils';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';
import api from '../../config/axios';

interface MonthGroup {
  period: string;
  year: number;
  month: number;
  count: number;
  totalAmount: number;
  invoices?: Invoice[];
  loading?: boolean;
}

const statusColors = {
  new: 'default',
  pending: 'warning',
  reviewed: 'info',
  ready_for_payment: 'primary',
  sent_to_bank: 'secondary',
  paid: 'success',
  paid_in_cash: 'success',
  rejected: 'error',
  archived: 'default'
} as const;

const getStatusLabel = (status: string) => {
  return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
};

const Archive: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { showError, showSuccess, AlertComponent } = useModernAlert();
  const { businessUnits, loading: businessUnitsLoading } = useBusinessUnits();
  
  const invoices = useSelector(selectInvoices);
  const loading = useSelector(selectInvoicesLoading);
  const pagination = useSelector(selectInvoicesPagination);
  const { user } = useSelector((state: RootState) => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [invoiceToUnarchive, setInvoiceToUnarchive] = useState<Invoice | null>(null);
  const [bulkUnarchiveDialogOpen, setBulkUnarchiveDialogOpen] = useState(false);
  
  // Month/Year folder state
  const [groups, setGroups] = useState<MonthGroup[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Fetch grouped data (folder structure)
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('grouped', 'true');
      if (searchTerm) params.append('search', searchTerm);
      if (businessUnitFilter !== 'all') params.append('businessUnitId', businessUnitFilter);
      
      const response = await api.get(`/invoices/archived?${params.toString()}`);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch archive groups:', error);
      showError('Nepodařilo se načíst archiv');
    } finally {
      setGroupsLoading(false);
    }
  }, [searchTerm, businessUnitFilter, showError]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch invoices for a specific period (when folder is expanded)
  const fetchPeriodInvoices = useCallback(async (period: string) => {
    const group = groups.find(g => g.period === period);
    if (!group || group.invoices) return; // Already loaded
    
    // Update group loading state
    setGroups(prev => prev.map(g => 
      g.period === period ? { ...g, loading: true } : g
    ));
    
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      params.append('limit', '1000'); // Get all invoices for the period
      if (searchTerm) params.append('search', searchTerm);
      if (businessUnitFilter !== 'all') params.append('businessUnitId', businessUnitFilter);
      
      const response = await api.get(`/invoices/archived?${params.toString()}`);
      
      // Update group with invoices
      setGroups(prev => prev.map(g => 
        g.period === period ? { ...g, invoices: response.data.invoices, loading: false } : g
      ));
    } catch (error) {
      console.error('Failed to fetch period invoices:', error);
      showError('Nepodařilo se načíst faktury');
      setGroups(prev => prev.map(g => 
        g.period === period ? { ...g, loading: false } : g
      ));
    }
  }, [groups, searchTerm, businessUnitFilter, showError]);

  const handleRefresh = () => {
    setExpandedFolders(new Set());
    setGroups([]);
    fetchGroups();
  };

  const handleSearch = () => {
    setExpandedFolders(new Set());
    setGroups([]);
    fetchGroups();
  };

  const handleUnarchive = async (invoice: Invoice) => {
    try {
      await dispatch(unarchiveInvoice({ id: invoice.id })).unwrap();
      showSuccess(czechTranslations.invoices.messages.unarchiveSuccess);
      setUnarchiveDialogOpen(false);
      handleRefresh();
    } catch (error: any) {
      showError(error || czechTranslations.invoices.messages.unarchiveError);
    }
  };

  const handleBulkUnarchive = async () => {
    try {
      for (const id of selectedInvoices) {
        await dispatch(unarchiveInvoice({ id })).unwrap();
      }
      showSuccess(czechTranslations.invoices.messages.bulkUnarchiveSuccess(selectedInvoices.length));
      setBulkUnarchiveDialogOpen(false);
      setSelectedInvoices([]);
      handleRefresh();
    } catch (error: any) {
      showError(error || czechTranslations.invoices.messages.bulkUnarchiveError);
    }
  };

  const handleSelectInvoice = (id: number) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(invId => invId !== id) : [...prev, id]
    );
  };

  const handleToggleFolder = (period: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(period)) {
      newExpanded.delete(period);
    } else {
      newExpanded.add(period);
      // Fetch invoices for this period if not already loaded
      fetchPeriodInvoices(period);
    }
    setExpandedFolders(newExpanded);
  };

  const handleExpandAll = () => {
    const allPeriods = new Set(groups.map(g => g.period));
    setExpandedFolders(allPeriods);
    // Fetch invoices for all periods
    groups.forEach(group => {
      if (!group.invoices) {
        fetchPeriodInvoices(group.period);
      }
    });
  };

  const handleCollapseAll = () => {
    setExpandedFolders(new Set());
  };

  const handleSelectAll = () => {
    const allInvoiceIds: number[] = [];
    groups.forEach(group => {
      if (group.invoices) {
        allInvoiceIds.push(...group.invoices.map(inv => inv.id));
      }
    });
    
    if (selectedInvoices.length === allInvoiceIds.length && allInvoiceIds.length > 0) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(allInvoiceIds);
    }
  };

  const renderInvoiceRow = (invoice: Invoice) => (
    <Box
      key={invoice.id}
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Checkbox
          checked={selectedInvoices.includes(invoice.id)}
          onChange={() => handleSelectInvoice(invoice.id)}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {invoice.invoiceNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoice.supplierName} → {invoice.customerName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateSafe(invoice.invoiceDate)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" color="primary">
            {invoice.totalAmount.toLocaleString('cs-CZ')} {invoice.currency}
          </Typography>
          <Chip 
            label={getStatusLabel(invoice.status)} 
            color={statusColors[invoice.status as keyof typeof statusColors] as any}
            size="small"
          />
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => navigate(`/invoices/${invoice.id}`)}
            title="Zobrazit"
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          <PDFDownload 
            invoiceId={invoice.id} 
            invoiceNumber={invoice.invoiceNumber}
            hasPdf={true}
          />
          <IconButton
            size="small"
            onClick={() => {
              setInvoiceToUnarchive(invoice);
              setUnarchiveDialogOpen(true);
            }}
            title={czechTranslations.invoices.actions.unarchive}
            color="primary"
          >
            <UnarchiveIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );

  // Calculate total selected across all folders
  const totalSelectedAcrossAllFolders = selectedInvoices.length;

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2, lg: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {AlertComponent}
      
      <Paper sx={{ p: { xs: 2, md: 2, lg: 3 }, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '1.75rem', lg: '2rem' } }}>
          📦 {czechTranslations.navigation.archive}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Archivované faktury organizované podle měsíce a roku
        </Typography>

        {/* Filters and Actions Bar */}
        <Grid container spacing={2} sx={{ mt: 2, mb: 3 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              placeholder="Hledat v archivu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Účetní jednotka</InputLabel>
              <Select
                value={businessUnitFilter}
                onChange={(e) => setBusinessUnitFilter(e.target.value)}
                label="Účetní jednotka"
              >
                <MenuItem value="all">Všechny</MenuItem>
                <MenuItem value="unassigned">Nepřiřazené</MenuItem>
                {businessUnits.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id.toString()}>
                    {bu.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                size={isTablet ? 'small' : 'medium'}
              >
                Hledat
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                size={isTablet ? 'small' : 'medium'}
              >
                Obnovit
              </Button>
              <Button
                variant="outlined"
                onClick={handleExpandAll}
                size={isTablet ? 'small' : 'medium'}
                disabled={groups.length === 0}
              >
                Rozbalit vše
              </Button>
              <Button
                variant="outlined"
                onClick={handleCollapseAll}
                size={isTablet ? 'small' : 'medium'}
                disabled={expandedFolders.size === 0}
              >
                Sbalit vše
              </Button>
              {totalSelectedAcrossAllFolders > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setBulkUnarchiveDialogOpen(true)}
                  startIcon={<UnarchiveIcon />}
                  size={isTablet ? 'small' : 'medium'}
                >
                  Obnovit ({totalSelectedAcrossAllFolders})
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Month/Year Folders */}
        {groupsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {czechTranslations.invoices.messages.noArchivedInvoices}
            </Typography>
          </Box>
        ) : (
          <Box>
            {groups.map((group) => (
              <Accordion
                key={group.period}
                expanded={expandedFolders.has(group.period)}
                onChange={() => handleToggleFolder(group.period)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: expandedFolders.has(group.period) ? 'action.hover' : 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                    {expandedFolders.has(group.period) ? (
                      <FolderOpenIcon color="primary" />
                    ) : (
                      <FolderIcon color="action" />
                    )}
                    <Typography variant="h6" sx={{ flex: 1, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                      {group.period}
                    </Typography>
                    <Chip 
                      label={`${group.count} ${group.count === 1 ? 'faktura' : group.count < 5 ? 'faktury' : 'faktur'}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right' }}>
                      {group.totalAmount.toLocaleString('cs-CZ')} Kč
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {group.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : group.invoices && group.invoices.length > 0 ? (
                    <Box>
                      {group.invoices.map(renderInvoiceRow)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Žádné faktury v tomto období
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>

      {/* Unarchive Single Invoice Dialog */}
      <Dialog open={unarchiveDialogOpen} onClose={() => setUnarchiveDialogOpen(false)}>
        <DialogTitle>🔄 {czechTranslations.invoices.actions.unarchive}</DialogTitle>
        <DialogContent>
          <Typography>
            Opravdu chcete obnovit fakturu {invoiceToUnarchive?.invoiceNumber} z archivu?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnarchiveDialogOpen(false)}>
            Zrušit
          </Button>
          <Button
            onClick={() => invoiceToUnarchive && handleUnarchive(invoiceToUnarchive)}
            variant="contained"
            color="primary"
          >
            Obnovit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Unarchive Dialog */}
      <Dialog open={bulkUnarchiveDialogOpen} onClose={() => setBulkUnarchiveDialogOpen(false)}>
        <DialogTitle>🔄 {czechTranslations.invoices.actions.bulkUnarchive}</DialogTitle>
        <DialogContent>
          <Typography>
            Opravdu chcete obnovit {selectedInvoices.length} vybraných faktur z archivu?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUnarchiveDialogOpen(false)}>
            Zrušit
          </Button>
          <Button
            onClick={handleBulkUnarchive}
            variant="contained"
            color="primary"
          >
            Obnovit vše
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Archive;

